import React, { Component } from 'react';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import propTypes from 'prop-types';
import { SAFARI } from '@utility/enums/browsers';
import { fnBrowserDetect } from '@utility/browsers';
import { clearGlobalSelection } from '@utility/clearGlobalSelection';
import PersonalInfoWrapper from '../../../../../UI/Editor/EditSpace/EditorPane/RenderContent/personalInfo';
import { Children } from '../../../../../UI/Editor/EditSpace/EditorPane/RenderContent/renderOrganization';
import PersonalInfoTitle from './title';
import ProfileImage from './profileImage';
import Phone from './phone';
import PersonalLinks from './personalLinks';
import BasicInput from './basicInput';
import Actions from '../../../../../../../../store/actions';
import { universityEditorSectionRestriction } from '../../../../../../../../utility/universityRestrictions';
import resetHistoryUtil from './resetHistoryUtil';

class PersonalInfo extends Component {
  state = {};

  /**
   * Since the first child as an input to personal info is name,
   * we would focus on it as soon as the page is mounted to personal info
   */
  setFirstInputEditorRef = (editor) => {
    this.firstChildEditorRef = editor;

    this.setFocusedEditor(editor);
  };

  setFocusedEditor = (nodeType, editor) => {
    const { updateFocusedEditor } = this.props;
    updateFocusedEditor(nodeType, editor);
  };

  updateEditorReference = (componentType) => {
    const { updateEditorRef } = this.props;
    updateEditorRef(componentType);
  };

  setImageUploadPath = async (file) => {
    const { documentId } = this.props;
    const ext = file.type.split('/')[1];
    const path = `resume/${documentId}/${uuidv4()}.${ext}`;
    return path;
  };

  componentDidMount = () => {
    // this.firstChildEditorRef.focus();

    const {
      fullName,
      tagline,
      phoneNumber,
      email,
      address,
      objective,
      personalLinks,
      resetHistory,
      documentPicture,
    } = this.props;

    resetHistoryUtil(
      resetHistory,
      fullName,
      tagline,
      phoneNumber,
      email,
      address,
      objective,
      personalLinks,
      documentPicture,
    );
  };

  componentWillUnmount = () => {
    if (fnBrowserDetect() === SAFARI) {
      clearGlobalSelection();
    }
  };

  render() {
    const {
      permission,
      saveDocument,
      formatType,
      setSaveToggler,
      editorRefs,
      statusBarRef,
      mostUsedToolSetRef,
      title,
      sectionKey,
      universityPlan,
    } = this.props;

    let showEditOption = true;
    let showImage = true;

    if (
      !universityEditorSectionRestriction(
        'document-picture',
        formatType,
        universityPlan,
      )
    ) {
      showEditOption = false;
    }
    if (
      !universityEditorSectionRestriction('image', formatType, universityPlan)
    ) {
      showImage = false;
    }

    return (
      <PersonalInfoWrapper>
        <PersonalInfoTitle
          sectionKey={sectionKey}
          title={title}
          editorRefs={editorRefs}
        />
        <Children
          nodeType="personalInfo"
          aria-label="Section header Personal Info"
        >
          {showImage && (
            <ProfileImage
              saveDocument={saveDocument}
              setSaveToggler={setSaveToggler}
              labelKey="document-picture"
              setImageUploadPath={this.setImageUploadPath}
              showEditOption={showEditOption}
            />
          )}
          <BasicInput
            label="Name"
            componentKey="fullName"
            setInputEditorRef={this.setFocusedEditor}
            permission={permission}
            saveDocument={saveDocument}
            formatType={formatType}
            editorRefs={editorRefs}
            showImage={showImage}
          />
          <BasicInput
            label="Tagline"
            componentKey="tagline"
            setInputEditorRef={this.setFocusedEditor}
            permission={permission}
            saveDocument={saveDocument}
            formatType={formatType}
            editorRefs={editorRefs}
            showImage={showImage}
            sectionKey={sectionKey}
          />
          <BasicInput
            label="Email"
            componentKey="email"
            setInputEditorRef={this.setFocusedEditor}
            permission={permission}
            saveDocument={saveDocument}
            formatType={formatType}
            editorRefs={editorRefs}
            showImage={showImage}
          />

          <Phone
            componentKey="phoneNumber"
            setInputEditorRef={this.setFocusedEditor}
            permission={permission}
            saveDocument={saveDocument}
            formatType={formatType}
            editorRefs={editorRefs}
            showImage={showImage}
          />
          <BasicInput
            label="Address"
            componentKey="address"
            setInputEditorRef={this.setFocusedEditor}
            permission={permission}
            saveDocument={saveDocument}
            editorRefs={editorRefs}
            formatType={formatType}
          />

          <PersonalLinks
            componentKey="personalLinks"
            setInputEditorRef={this.setFocusedEditor}
            permission={permission}
            saveDocument={saveDocument}
            formatType={formatType}
            editorRefs={editorRefs}
            statusBarRef={statusBarRef}
            mostUsedToolSetRef={mostUsedToolSetRef}
          />

          {universityEditorSectionRestriction(
            'objective',
            formatType,
            universityPlan,
          ) && (
            <BasicInput
              label="Objective"
              componentKey="objective"
              setInputEditorRef={this.setFocusedEditor}
              permission={permission}
              saveDocument={saveDocument}
              editorRefs={editorRefs}
              formatType={formatType}
            />
          )}
        </Children>
      </PersonalInfoWrapper>
    );
  }
}

PersonalInfo.propTypes = {
  fullName: propTypes.objectOf(propTypes.object).isRequired,
  tagline: propTypes.objectOf(propTypes.object).isRequired,
  phoneNumber: propTypes.objectOf(propTypes.object).isRequired,
  email: propTypes.objectOf(propTypes.object).isRequired,
  address: propTypes.objectOf(propTypes.object).isRequired,
  objective: propTypes.objectOf(propTypes.object).isRequired,
  personalLinks: propTypes.objectOf(propTypes.object).isRequired,
  resetHistory: propTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  documentId: state.editResumeFormReducer.documentId,
  history: state.editResumeFormReducer.history,
  fullName: state.editResumeFormReducer.fullName,
  tagline: state.editResumeFormReducer.tagline,
  email: state.editResumeFormReducer.email,
  address: state.editResumeFormReducer.address,
  objective: state.editResumeFormReducer.objective,
  phoneNumber: state.editResumeFormReducer.phoneNumber,
  personalLinks: state.editResumeFormReducer.personalLinks,
  documentPicture: state.editResumeFormReducer.documentPicture,
  universityPlan: state.userInfoReducer.universityPlan,
});

const mapDispatchToProps = (dispatch) => ({
  resetHistory: (params) =>
    dispatch(Actions.editResumeFormActions.resetHistory(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PersonalInfo);
